import { useMemo } from 'react';
import { EventPageType } from '@graphql/generated-base';
import type { BoxProps } from '@withjoy/joykit';
import type { PageDimensions, PageSpecificHorizontalPadding, PageWidthCategory } from './usePageDimensions.types';
import { pxToRem } from '@withjoy/joykit/theme';

/**
 * Standardized page content widths for guest site layouts.
 *
 * Width categories:
 * - narrow (456px): Welcome page date/time/location components
 * - medium (680px): Default for most content pages (FAQ, Story, VIP, Apps)
 * - mediumWide (860px): Custom pages that are wider than medium but not as wide as wide
 * - wide (1032px): Travel content, Schedule content
 * - fullWidth: Travel map/accommodations, Registry
 */
const PAGE_CONTENT_MAX_WIDTHS: Readonly<Record<PageWidthCategory, string | undefined>> = {
  narrow: pxToRem(456),
  medium: pxToRem(680),
  mediumWide: pxToRem(860),
  wide: pxToRem(1032),
  fullWidth: undefined,
};

/**
 * Responsive page padding based on viewport width.
 * - Mobile: 32px horizontal padding
 * - Up to 1440px: 64px horizontal padding
 */
const PAGE_HORIZONTAL_PADDING = {
  MOBILE: 32,
  DESKTOP: 64,
} as const;

const PAGE_SPECIFIC_HORIZONTAL_PADDING = {
  accommodations: {
    MOBILE: 12,
    DESKTOP: 12,
  },
} as const;

/**
 * Maps page types and slugs to their width category.
 * - narrow: Welcome page components (date/time/location)
 * - medium: FAQ, Story, VIP, Apps, most Custom pages
 * - wide: Schedule, Travel content areas
 * - fullWidth: Registry, Accommodations map
 */
const getPageWidthCategory = (pageType: EventPageType, pageSlug?: string): PageWidthCategory => {
  switch (pageType) {
    case EventPageType.welcome:
      return 'narrow';

    case EventPageType.schedule:
    case EventPageType.travel:
      return 'wide';

    case EventPageType.registry:
    case EventPageType.vip:
      return 'fullWidth';

    case EventPageType.custom:
      return pageSlug === 'accommodations' ? 'fullWidth' : 'mediumWide';

    case EventPageType.faq:
    case EventPageType.story:
    case EventPageType.app:
    default:
      return 'medium';
  }
};

const getResponsiveHorizontalPadding = (pageSlug?: string): BoxProps['padding'] => {
  const specificPadding = pageSlug ? PAGE_SPECIFIC_HORIZONTAL_PADDING[pageSlug as keyof typeof PAGE_SPECIFIC_HORIZONTAL_PADDING] : undefined;

  const padding = specificPadding ?? PAGE_HORIZONTAL_PADDING;

  return {
    _: pxToRem(padding.MOBILE),
    sm4: pxToRem(padding.DESKTOP),
  };
};

/**
 * Returns memoized page dimension values based on page type and slug.
 * Use this hook to get consistent page widths and padding across the layout.
 * Returns default 'medium' dimensions when pageType is undefined.
 */
export const usePageDimensions = (pageType: EventPageType | undefined, pageSlug?: string): PageDimensions => {
  return useMemo(() => {
    const category = pageType ? getPageWidthCategory(pageType, pageSlug) : 'medium';
    return {
      category,
      isFullWidth: category === 'fullWidth',
      maxWidth: PAGE_CONTENT_MAX_WIDTHS[category],
      horizontalPadding: getResponsiveHorizontalPadding(pageSlug),
    };
  }, [pageType, pageSlug]);
};

export const getPageHorizontalPadding = (pageSlug: keyof typeof PAGE_SPECIFIC_HORIZONTAL_PADDING): PageSpecificHorizontalPadding => {
  return PAGE_SPECIFIC_HORIZONTAL_PADDING[pageSlug];
};
